import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Lock, KeyRound } from "lucide-react";
import { api, apiErrorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Field, TextInput } from "@/components/site/Field";
import { IMAGES } from "@/lib/data";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const nav = useNavigate();
  const { setFromToken } = useAuth();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) return toast.error("Password must be at least 8 characters.");
    if (password !== confirm) return toast.error("Passwords don't match.");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/reset-password", { token, password });
      setFromToken(data.user);
      toast.success("Password reset — you're signed in");
      nav("/admin");
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-muted flex items-center justify-center px-5">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img src={IMAGES.logo} alt="Harbour Haze" className="h-16 w-16 mx-auto object-contain" />
          <h1 className="mt-4 font-head font-black text-3xl tracking-tight">Reset password</h1>
          <p className="overline text-foreground/50 mt-1">Harbour Haze Admin</p>
        </div>

        {!token ? (
          <div className="bg-background border-2 border-foreground rounded-2xl shadow-brutal-lg p-8 text-center">
            <p className="font-head font-bold text-lg">This reset link is invalid.</p>
            <p className="text-foreground/60 text-sm mt-2">Request a new one from the login page.</p>
            <a href="/admin/login" className="inline-block mt-5 font-mono-plex text-xs text-foreground/50 hover:text-foreground">← Go to login</a>
          </div>
        ) : (
          <form onSubmit={submit} className="bg-background border-2 border-foreground rounded-2xl shadow-brutal-lg p-8 space-y-5" data-testid="reset-form">
            <div className="flex items-center gap-3 bg-secondary/15 border-2 border-foreground rounded-xl px-4 py-3">
              <KeyRound size={20} className="text-secondary" />
              <p className="font-mono-plex text-xs text-foreground/70">Choose a new password for your account.</p>
            </div>
            <Field label="New password"><TextInput data-testid="reset-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="At least 8 characters" autoComplete="new-password" /></Field>
            <Field label="Confirm password"><TextInput data-testid="reset-confirm" type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Repeat password" autoComplete="new-password" /></Field>
            <button type="submit" disabled={loading} data-testid="reset-submit"
              className="w-full font-head font-bold text-lg bg-foreground text-background rounded-full py-4 flex items-center justify-center gap-2 hover:bg-primary hover:text-foreground transition-colors disabled:opacity-60">
              <Lock size={16} /> {loading ? "Resetting…" : "Set new password"}
            </button>
          </form>
        )}
        <a href="/admin/login" className="block text-center mt-6 font-mono-plex text-xs text-foreground/50 hover:text-foreground">← Back to login</a>
      </div>
    </div>
  );
}
